import { t } from '@/i18n'

import { BODY_FONT } from '../text.utils'
import type { TicketElement } from '../TicketElement'
import type { ProductStock } from '../types'

export class ProductsStockBreakdown implements TicketElement {
  products: ProductStock[]
  lineHeight: number
  topPadding: number
  fontSize: number
  extraSpaceAfterHeader: number

  constructor(products: ProductStock[]) {
    this.products = products
    this.lineHeight = 26
    this.topPadding = 10
    this.fontSize = 12
    this.extraSpaceAfterHeader = 10
  }

  height(): number {
    const headerRowHeight = this.lineHeight
    const productsHeight = this.lineHeight * this.products.length
    return this.topPadding + headerRowHeight + productsHeight + this.extraSpaceAfterHeader
  }

  private truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    const ellipsis = '...'
    const ellipsisWidth = ctx.measureText(ellipsis).width

    if (ctx.measureText(text).width <= maxWidth) {
      return text
    }

    let truncatedText = text
    while (truncatedText.length > 0) {
      truncatedText = truncatedText.substring(0, truncatedText.length - 1)
      const textWidth = ctx.measureText(truncatedText).width
      if (textWidth + ellipsisWidth <= maxWidth) {
        return truncatedText + ellipsis
      }
    }
    return ellipsis
  }

  render(top: number, ctx: CanvasRenderingContext2D, totalWidth: number): void {
    top += this.topPadding
    ctx.textBaseline = 'top'

    const columnWeights = {
      name: 3.2,
      initial: 1,
      current: 1,
      toRestock: 1
    }
    const totalWeight = Object.values(columnWeights).reduce((sum, weight) => sum + weight, 0)

    const horizontalPadding = 5
    const effectiveWidth = totalWidth - horizontalPadding * 2

    const unitWidth = effectiveWidth / totalWeight

    const columnWidths = {
      name: unitWidth * columnWeights.name,
      initial: unitWidth * columnWeights.initial,
      current: unitWidth * columnWeights.current,
      toRestock: unitWidth * columnWeights.toRestock
    }

    const columnCenters = {
      name: horizontalPadding + columnWidths.name / 2,
      initial: horizontalPadding + columnWidths.name + columnWidths.initial / 2,
      current:
        horizontalPadding + columnWidths.name + columnWidths.initial + columnWidths.current / 2,
      toRestock:
        horizontalPadding +
        columnWidths.name +
        columnWidths.initial +
        columnWidths.current +
        columnWidths.toRestock / 2
    }

    const headerY = top
    ctx.fillStyle = 'black'
    ctx.fillRect(0, headerY, totalWidth, this.lineHeight)

    ctx.fillStyle = 'white'
    ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`

    const textVerticalOffset = (this.lineHeight - this.fontSize) / 2

    ctx.textAlign = 'left'
    ctx.fillText(t('report.product'), horizontalPadding, headerY + textVerticalOffset)

    ctx.textAlign = 'center'
    ctx.fillText(
      t('report.stock.initial-short'),
      columnCenters.initial,
      headerY + textVerticalOffset
    )
    ctx.fillText(
      t('report.stock.current-short'),
      columnCenters.current,
      headerY + textVerticalOffset
    )
    ctx.fillText(
      t('report.stock.to-restock-short'),
      columnCenters.toRestock - horizontalPadding,
      headerY + textVerticalOffset
    )

    top += this.lineHeight
    top += this.extraSpaceAfterHeader

    ctx.fillStyle = 'black'
    ctx.font = `${this.fontSize}px ${BODY_FONT}`

    const maxNameWidth = columnWidths.name - horizontalPadding

    this.products.forEach((product: ProductStock, index: number) => {
      const productName = this.truncateText(ctx, product.name, maxNameWidth)
      const productY = top + (this.lineHeight - this.fontSize) / 2

      ctx.textAlign = 'left'
      ctx.fillText(productName, horizontalPadding, productY)

      ctx.textAlign = 'center'
      ctx.fillText(product.initial.toString(), columnCenters.initial, productY)
      ctx.fillText(product.current.toString(), columnCenters.current, productY)
      ctx.fillText(
        product.toRestock.toString(),
        columnCenters.toRestock - horizontalPadding,
        productY
      )

      if (index < this.products.length - 1) {
        const lineY = top + this.lineHeight
        ctx.save()
        ctx.setLineDash([2, 3])
        ctx.beginPath()
        ctx.moveTo(0, lineY)
        ctx.lineTo(totalWidth, lineY)
        ctx.stroke()
        ctx.restore()
      }

      top += this.lineHeight
    })
  }
}
